import {Component, computed, effect, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TourMatMenuModule} from 'ngx-ui-tour-md-menu';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {Subscription} from 'rxjs';
import {LegendService} from '../../api/services/legend.service';
import {MapService} from '../../api/services/map.service';
import {ModelService} from '../../api/services/model.service';
import {OpenaiService} from '../../api/services/openai.service';
import {JobService} from '../../api/services/job.service';
import {JobStatus} from '../../api/interfaces/jobs.dto';
import {NotificationService} from '../../services/notification.service';
import {LegendDto, LegendModelDto, ModelElementType} from '@maporium-workspace/shared';
import {isInIframe} from '../helpers/maporium.validators';
import {buildLinkShapeSvg, buildNodeShapeSvg} from '../helpers/legend-shape.helper';
import {AiStateService} from '../../services/ai-state.service';
import {MapRoleService} from '../../acl/map-role.service';

const LEGEND_OPEN_KEY = 'maporium_legend_open';
const LEGEND_SEEN_PREFIX = 'maporium_legend_seen_';

interface LegendItemView {
  id: string;
  name: string;
  description: string;
  icon: SafeHtml;
}

@Component({
  selector: 'maporium-legend-panel',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, TourMatMenuModule, TranslatePipe],
  styleUrls: ['legend-panel.component.scss'],
  template: `
    <!-- Toggle button when legend is closed -->
    <button *ngIf="!isOpen && (!isIframe || hasLegend)"
            class="legend-toggle-btn"
            tourAnchor="legend-anchor"
            (click)="toggle()"
            [matTooltip]="'LEGEND_PANEL.SHOW_LEGEND_TOOLTIP' | translate"
            matTooltipPosition="right">
      <i class="icon-smap-object-legend"></i>
    </button>

    <!-- Legend panel when open -->
    <div class="legend-panel" tourAnchor="legend-anchor" *ngIf="isOpen && (!isIframe || hasLegend)">
      <div class="legend-header">
        <span class="legend-title">
          <i class="icon-smap-object-legend help-icon"></i>
          {{ 'LEGEND_PANEL.TITLE' | translate }}
        </span>
        <span class="close-btn"
              (click)="toggle()"
              [matTooltip]="'LEGEND_PANEL.HIDE_LEGEND_TOOLTIP' | translate"
              matTooltipPosition="right">&times;</span>
      </div>

      <div class="legend-content" *ngIf="nodeItems.length > 0 || linkItems.length > 0; else emptyLegend">
        <!-- Node models -->
        <ng-container *ngIf="nodeItems.length > 0">
          <div *ngFor="let item of nodeItems; trackBy: trackByItemId"
               class="legend-item"
               [matTooltip]="item.description"
               matTooltipPosition="right"
               matTooltipClass="legend-item-tooltip">
            <div class="legend-icon" [innerHTML]="item.icon"></div>
            <span class="legend-label">{{ item.name }}</span>
          </div>
        </ng-container>

        <!-- Separator if both types exist -->
        <div *ngIf="nodeItems.length > 0 && linkItems.length > 0"
             style="border-top: 1px solid rgba(0,0,0,0.08); margin: 6px 0;">
        </div>

        <!-- Link models -->
        <ng-container *ngIf="linkItems.length > 0">
          <div *ngFor="let item of linkItems; trackBy: trackByItemId"
               class="legend-item"
               [matTooltip]="item.description"
               matTooltipPosition="right"
               matTooltipClass="legend-item-tooltip">
            <div class="legend-icon" [innerHTML]="item.icon"></div>
            <span class="legend-label">{{ item.name }}</span>
          </div>
        </ng-container>
      </div>

      <ng-template #emptyLegend>
        <div class="legend-empty">
          {{ 'LEGEND_PANEL.EMPTY' | translate }}
          <button *ngIf="canEdit() && aiStateService.isAiVisible()"
                  class="legend-generate-btn"
                  (click)="generateLegend()"
                  [disabled]="isGenerating"
                  [matTooltip]="'LEGEND_PANEL.GENERATE_TOOLTIP' | translate"
                  matTooltipPosition="right">
            {{ (isGenerating ? 'LEGEND_PANEL.GENERATING' : 'LEGEND_PANEL.GENERATE') | translate }}
          </button>
        </div>
      </ng-template>
    </div>
  `
})
export class LegendPanelComponent implements OnInit, OnDestroy {
  isOpen = false;
  isIframe = isInIframe();
  legend: LegendDto | null = null;
  nodeItems: LegendItemView[] = [];
  linkItems: LegendItemView[] = [];
  isGenerating = false;

  // In smaplet mode we only render the legend UI when the smap actually has a
  // legend with included models. Updated alongside node/link items (MAP-1245).
  get hasLegend(): boolean {
    return this.nodeItems.length > 0 || this.linkItems.length > 0;
  }

  private subscriptions: Subscription[] = [];

  canEdit = computed(() => {
    const map = this.mapService.selectedMap();
    if (!map || isInIframe()) return false;
    return this.mapRoleService.hasCurrentUserPermission('edit', map) ||
      this.mapRoleService.hasCurrentUserPermission('manage', map);
  });

  constructor(
    protected aiStateService: AiStateService,
    private legendService: LegendService,
    private mapService: MapService,
    private modelService: ModelService,
    private sanitizer: DomSanitizer,
    private openaiService: OpenaiService,
    private jobService: JobService,
    private notificationService: NotificationService,
    private mapRoleService: MapRoleService,
    private translate: TranslateService
  ) {
    // Load legend when map changes (signal-based, must be in constructor)
    effect(() => {
      const map = this.mapService.selectedMap();
      if (map?.id) {
        this.legendService.get(map.id).subscribe();
      } else {
        this.legendService.clearCurrentLegend();
      }
    });
  }

  ngOnInit() {
    // Restore open state from localStorage
    this.isOpen = localStorage.getItem(LEGEND_OPEN_KEY) === 'true';

    // External consumers (onboarding tour) can request the panel open via
    // LegendService.requestOpen() without a direct component reference.
    this.subscriptions.push(
      this.legendService.openRequest$.subscribe(() => {
        // In iframe, only honor the open request when the smap actually has
        // a legend (otherwise the panel would render an empty/Generate UI,
        // which doesn't belong in smaplet context — MAP-1245).
        if (!this.isOpen && (!this.isIframe || this.hasLegend)) {
          this.isOpen = true;
          localStorage.setItem(LEGEND_OPEN_KEY, 'true');
        }
      })
    );

    // Update local state when legend changes
    const legendSub = this.legendService.currentLegendSubject.subscribe((legend: LegendDto | null) => {
      this.legend = legend;
      this.updateModels();

      // Auto-open on first view of a smap that has legend models
      if (legend?.mapId && (this.nodeItems.length > 0 || this.linkItems.length > 0)) {
        const seenKey = LEGEND_SEEN_PREFIX + legend.mapId;
        if (!localStorage.getItem(seenKey)) {
          localStorage.setItem(seenKey, 'true');
          this.isOpen = true;
          localStorage.setItem(LEGEND_OPEN_KEY, 'true');
        }
      }
    });
    this.subscriptions.push(legendSub);

    // Refresh legend on explicit model-CRUD events
    const modelsSub = this.modelService.modelsChangedSubject.subscribe(() => {
      const map = this.mapService.getCurrentSelectedMapFromStore();
      if (map?.id) {
        this.legendService.get(map.id).subscribe();
      }
    });
    this.subscriptions.push(modelsSub);

    // Also react to the models cache being refreshed (e.g. assistant "add missing
    // legends" path, which loads models via getAll but may not emit modelsChangedSubject).
    let lastModelsSignature = '';
    const currentModelsSub = this.modelService.currentModelsSubject.subscribe((models) => {
      const signature = models.nodeModels.map(m => m.id).join(',') + '|' +
        models.linkModels.map(m => m.id).join(',');
      if (signature === lastModelsSignature) return;
      lastModelsSignature = signature;
      const map = this.mapService.getCurrentSelectedMapFromStore();
      if (map?.id) {
        this.legendService.get(map.id).subscribe();
      }
    });
    this.subscriptions.push(currentModelsSub);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  toggle() {
    this.isOpen = !this.isOpen;
    localStorage.setItem(LEGEND_OPEN_KEY, String(this.isOpen));
  }

  generateLegend(): void {
    if (this.isGenerating) return;
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (!mapId) return;

    this.isGenerating = true;
    this.openaiService.generateModels(mapId).subscribe({
      next: (job) => {
        this.jobService.trackJob(job.id, (updated) => {
          if (updated.status === JobStatus.COMPLETED) {
            this.isGenerating = false;
            this.legendService.get(mapId).subscribe();
            this.modelService.modelsChangedSubject.next();
            const created = updated.result?.rawData?.modelsCreated ?? 0;
            if (created > 0) {
              this.notificationService.success(
                this.translate.instant(
                  created === 1 ? 'LEGEND_PANEL.MODELS_ADDED_SINGULAR' : 'LEGEND_PANEL.MODELS_ADDED_PLURAL',
                  {count: created}
                )
              );
            } else {
              this.notificationService.error(this.translate.instant('LEGEND_PANEL.NO_GROUPINGS'));
            }
          } else if (updated.status === JobStatus.FAILED) {
            this.isGenerating = false;
            this.notificationService.error(updated.error || this.translate.instant('LEGEND_PANEL.GENERATE_FAILED'));
          }
        });
      },
      error: () => {
        this.isGenerating = false;
        this.notificationService.error(this.translate.instant('LEGEND_PANEL.GENERATE_FAILED'));
      }
    });
  }

  trackByItemId(_index: number, item: LegendItemView): string {
    return item.id;
  }

  private updateModels() {
    if (!this.legend?.legendModels) {
      this.nodeItems = [];
      this.linkItems = [];
      return;
    }

    // Only include models that have a backing model and are included in the legend.
    // Excluded models are hidden entirely (dimming is reserved for future filter behavior).
    const included = this.legend.legendModels.filter(lm => lm.model && lm.included);

    this.nodeItems = included
      .filter(lm => lm.model.elementType === ModelElementType.NODE)
      .sort((a, b) => a.model.name.localeCompare(b.model.name))
      .map(lm => this.toNodeView(lm));

    this.linkItems = included
      .filter(lm => lm.model.elementType === ModelElementType.LINK)
      .sort((a, b) => a.model.name.localeCompare(b.model.name))
      .map(lm => this.toLinkView(lm));
  }

  // Precompute icons once per updateModels run so template bindings hand back
  // a stable reference each change-detection cycle. Recomputing in a template
  // binding (method returning a fresh SafeHtml) drives Angular into a loop.
  private toNodeView(lm: LegendModelDto): LegendItemView {
    return {
      id: lm.model.id,
      name: lm.model.name,
      description: lm.model.description || '',
      icon: this.sanitizer.bypassSecurityTrustHtml(buildNodeShapeSvg(lm.model.elementDefinition?.originalElement))
    };
  }

  private toLinkView(lm: LegendModelDto): LegendItemView {
    return {
      id: lm.model.id,
      name: lm.model.name,
      description: lm.model.description || '',
      icon: this.sanitizer.bypassSecurityTrustHtml(buildLinkShapeSvg(lm.model.elementDefinition?.originalElement))
    };
  }
}
